'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CheckCircle2, Send } from 'lucide-react'

export const SUPPORT_EMAIL = 'support@ribbitmoney.com'

const TOPICS = [
  { value: 'general', label: 'General question' },
  { value: 'account', label: 'Account & billing' },
  { value: 'connections', label: 'Bank & investment connections' },
  { value: 'security', label: 'Security & privacy' },
  { value: 'bug', label: 'Report a bug' },
] as const

export function ContactForm() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [topic, setTopic] = useState<string>('general')
  const [message, setMessage] = useState('')
  const [sent, setSent] = useState(false)

  const topicLabel = TOPICS.find((t) => t.value === topic)?.label ?? 'General question'

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const subject = `[Ribbit Support] ${topicLabel}`
    const body = [
      message.trim(),
      '',
      '—',
      `From: ${name.trim() || '(no name provided)'}`,
      `Reply to: ${email.trim() || '(no email provided)'}`,
      `Topic: ${topicLabel}`,
    ].join('\n')

    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`

    setSent(true)
  }

  if (sent) {
    return (
      <div className="rounded-xl border bg-card p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle2 className="h-6 w-6 text-primary" />
        </div>
        <h2 className="font-brand text-2xl tracking-tight">Almost there</h2>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
          Your email app should have opened with your message pre-filled. Just hit send and we&apos;ll
          get back to you. If nothing opened, email us directly at{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-foreground underline underline-offset-4 hover:text-primary transition-colors"
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
        <Button variant="outline" className="mt-6" onClick={() => setSent(false)}>
          Write another message
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-xl border bg-card p-6 md:p-8 space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            autoComplete="name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">
            Email <span className="text-muted-foreground font-normal">(so we can reply)</span>
          </Label>
          <Input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="topic">Topic</Label>
        <Select value={topic} onValueChange={setTopic}>
          <SelectTrigger id="topic">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TOPICS.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Message</Label>
        <Textarea
          id="message"
          required
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="How can we help?"
          className="resize-y"
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          This opens your email app with the message ready to send.
        </p>
        <Button type="submit" className="gap-2">
          <Send className="h-4 w-4" />
          Send message
        </Button>
      </div>
    </form>
  )
}
